import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PhoneIcon } from '@rsos/assets/icons';
import { Box, Button, Text } from '@rsos/components/capstone/base';
import {
  RCS_CONVERSATION_STATUS_ACTIVE,
  RCS_CONVERSATION_STATUS_PENDING,
} from '@rsos/constants/rcsStatuses';
import { RCS_CONVERSATION_STATUS_ENDED } from '@rsos/constants/rcsStatuses';
import { useSelectedRcsConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSelectors';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import useHasAccessToUniteEventPane from '../../../../hooks/useHasAccessToUniteEventPane';
import { useChatContext } from '../ChatContext';
import { CollapseButton } from '../SharedChatUI/CollapseButton';
import { TranslationMenu } from '../SharedChatUI/TranslationMenu';
import { TranslationMenuButton } from '../SharedChatUI/TranslationMenuButton';
import RcsAcceptDeclineChat from './RcsAcceptDeclineChat';
import { HeaderContainer, RedCircle, IconButtonsBox } from './RcsChat.styles';
import RcsEndChat from './RcsEndChat';
import RcsStartChatAccordion from './RcsStartChatAccordion';
import {
  CHAT_HEADER_START,
  CHAT_HEADER_END,
  CHAT_HEADER_ACCEPT_DECLINE,
} from './constants';

/**
 * Displays a chat header above the chat box.
 * @param {Boolean} isChatBoxVisible - Flag for expanding/collapsing the chat
 * @param {Boolean} isForOverview - Flag for checking if the component is used for the overview tab
 * @param {Function} onClickShowAccordion - Onclick handler for toggling the accordion for preconfigured messages
 * @param {Function} onStartConversation - Onclick handler for starting the conversation
 * @param {String} showButtons - Type of buttons to show (START_CHAT/END_CHAT/ACCEPT_DECLINE_CHAT)
 * @param {Boolean} showAcceptDeclineChat - Flag for showing accept/decline buttons
 * @param {Boolean} showPreconfiguredMessages - Flag for showing preconfigured messages to start the chat with
 * @param {Boolean} isOutboundSMSConversation - Flag for if conversation is from OutboundSMS
 */
const RcsChatHeader = ({
  isForOverview = false,
  onClickShowAccordion,
  onStartConversation,
  showButtons,
  showPreconfiguredMessages,
}) => {
  const isChatBoxVisible = !useChatContext().isCollapsed;

  const hasAccessToUniteEventPane = useHasAccessToUniteEventPane();

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const rcsConversation = useSelectedRcsConversation(selectedCallerID);

  const displayPhoneNumber =
    formatInternationalPhoneNumberHuman(selectedCallerID);

  const isFetchingMetadata = useMemo(
    () => rcsConversation?.loading?.getConversationMetadata,
    [rcsConversation],
  );

  const headerText = useMemo(() => {
    const text = {
      [RCS_CONVERSATION_STATUS_PENDING]: (
        <>
          <RedCircle /> Google RCS
        </>
      ),
      [RCS_CONVERSATION_STATUS_ACTIVE]: (
        <>
          <RedCircle /> Google RCS
        </>
      ),
      [RCS_CONVERSATION_STATUS_ENDED]: <>Closed Google RCS</>,
    };
    if (!rcsConversation || !rcsConversation.data) return '';
    return text[rcsConversation.data.conversationStatus] || null;
  }, [rcsConversation]);

  const renderHeaderContent = () => {
    if (isFetchingMetadata) {
      return (
        <Box width="100%" height="100%">
          <Box
            alignSelf="center"
            horizontal
            width="100%"
            borderRadius="0 0 5px 5px"
          >
            <Button
              btnType="tertiary"
              disabled={isFetchingMetadata}
              isLoadingProp={isFetchingMetadata}
              noMargin
              size="med"
              grow
              width="100%"
            />
          </Box>
        </Box>
      );
    }

    const buttonComponents = {
      [CHAT_HEADER_START]: (
        <RcsStartChatAccordion
          isUniteEventPaneEnabled={hasAccessToUniteEventPane}
          isForOverview={isForOverview}
          onClickShowAccordion={onClickShowAccordion}
          onStartConversation={onStartConversation}
          showPreconfiguredMessages={showPreconfiguredMessages}
        />
      ),
      [CHAT_HEADER_END]: (
        <Box horizontal gridColumnGap="8px" width="100%">
          <RcsEndChat
            isChatBoxVisible={isChatBoxVisible}
            callerID={selectedCallerID}
          />
        </Box>
      ),
      [CHAT_HEADER_ACCEPT_DECLINE]: (
        <Box horizontal gridColumnGap="8px" width="100%">
          <RcsAcceptDeclineChat />
        </Box>
      ),
    };

    return buttonComponents[showButtons] || null;
  };

  return (
    <HeaderContainer
      data-name="text-from-911-chat-header"
      pb={showButtons ? '4px' : '12px'}
      pt={isForOverview ? '16px' : '12px'}
    >
      <Box horizontal justify="space-between" width="100%">
        <Box>
          <Text inline fontWeight="bold">
            <Text color="primaryText" size="normal">
              {headerText}
            </Text>
            <Box horizontal>
              {!hasAccessToUniteEventPane && (
                <Text color="secondaryText">
                  <PhoneIcon
                    width={10}
                    height={12}
                    viewboxWidth={11}
                    viewboxHeight={15}
                  />
                </Text>
              )}
              <Text
                color="secondaryText"
                fontWeight={hasAccessToUniteEventPane ? 'regular' : 'inherit'}
                size={hasAccessToUniteEventPane ? 'xsmall' : 'small'}
              >
                {displayPhoneNumber}
              </Text>
            </Box>
          </Text>
        </Box>
        <IconButtonsBox>
          <TranslationMenuButton />
          <CollapseButton />
        </IconButtonsBox>
      </Box>
      <TranslationMenu />
      {renderHeaderContent()}
    </HeaderContainer>
  );
};
export default RcsChatHeader;
